/**
 * sectionMarkdown — turn a section captured from the live Word document back
 * into the markdown the SSOT stores, WITHOUT losing its tables or figures.
 *
 * Why this exists (2026-09-28): Save / auto-save used to persist a section as
 * the plain text of its paragraphs. Word flattens a table into one paragraph
 * per cell and a picture has no text at all, so every save turned each table
 * into a column of lines and dropped every figure — and the next Load rebuilt
 * the document from that. This module is the pure half of the fix: the Word
 * reader (``readSectionsForSave`` in ``wordWriter.ts``) gathers a structural
 * snapshot of the section; this builds markdown from it.
 *
 *   - A table becomes a pipe table again (cell markup such as ``**bold**`` is
 *     kept from the SSOT copy wherever the cell's text is unchanged).
 *   - A drafted figure becomes the ``[IMAGE DATA: <caption>]`` pointer it was
 *     rendered from. Its caption comes from the identity stamped on the picture
 *     at insert time, or — for pictures inserted before stamping existed — is
 *     recovered from the SSOT's own markers.
 *   - A picture the writer inserted by hand is titled (its "Figure N. …"
 *     caption line, else its alt text, else ``Figure <section>-<k>``) and
 *     returned in ``uploads``: the caller stores the bytes under that title and
 *     the section points at it with the same ``[IMAGE DATA: <title>]`` pointer,
 *     so every Load inserts it on the spot exactly like a drafted figure.
 *
 * Pure — no Office.js — so it is unit-testable.
 */

/** ``altTextTitle`` stamped on every figure Asthra inserts. The server's
 *  ``word_writer.ASTHRA_FIGURE_ALT_TITLE`` is the same string. A title of
 *  ``asthra-figure|<url>`` marks a ``![alt](<url>)`` figure (url kept). */
export const ASTHRA_FIGURE_ALT_TITLE = "asthra-figure";

/** One inline picture, in document order within its section. */
export interface CapturedPicture {
  /** Position among the section's pictures (document order) — the handle the
   *  caller uses to stamp identities back onto the right picture. */
  ordinal: number;
  altTitle: string;
  altDescription: string;
  /** Raw base64 (no ``data:`` prefix). Only read for pictures that carry no
   *  Asthra stamp — a stamped picture never needs its bytes. */
  base64: string | null;
  /** Word's ``imageFormat`` lower-cased (``png``, ``jpeg``…). */
  format: string;
  /** Word web's ~850-byte "still loading" bitmap — not the real picture yet. */
  loadingPlaceholder: boolean;
}

export interface CapturedParagraph {
  /** Trimmed paragraph text. */
  text: string;
  /** Set on a Word heading paragraph: its section number (list label, else the
   *  number its text starts with), or "" when it has none. */
  headingNumber?: string;
  /** Set when the paragraph is inside a table cell. ``t`` numbers the tables
   *  within the section so two adjacent tables stay two tables. */
  table?: { t: number; row: number; col: number };
  pictures?: CapturedPicture[];
}

export interface CapturedSection {
  sectionNumber: string;
  paragraphs: CapturedParagraph[];
}

export interface FigureUpload {
  ordinal: number;
  caption: string;
  base64: string;
  format: string;
}

export interface FigureStamp {
  ordinal: number;
  title: string;
  description: string;
}

export interface BuiltSection {
  markdown: string;
  /** Hand-inserted pictures to store under their caption before saving. */
  uploads: FigureUpload[];
  /** Identities to stamp onto pictures that lack one (best-effort). */
  stamps: FigureStamp[];
  /** A hand-inserted picture is still Word's loading placeholder — its bytes
   *  can't be stored yet, so the caller must NOT save this section this time
   *  (saving would drop the figure). Retried on the next save. */
  deferred: boolean;
}

const RE_IMAGE_DATA = /^\[\s*image(?:\s+data)?\s*[:–-]\s*(.+?)\s*\]$/i;
const RE_IMAGE_MD = /^!\[([^\]]*)\]\(([^)]+)\)$/;
const RE_TABLE_SEP = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/;
const RE_FIGURE_CAPTION = /^(?:figure|fig\.?)\s*[a-z0-9]/i;
const RE_ADDIN_PLACEHOLDER = /^\[Image:\s*(.+?)\s*\]$/;
const RE_BRACKETED = /^\[(.+)\]$/;

/** Content identity — the same collapse the Save change-check uses. */
export function normKey(s: string): string {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

interface SsotMarker {
  line: string; // the exact marker line, e.g. "[IMAGE DATA: X]" or "![X](url)"
  captionKey: string;
  contextKey: string; // normalised previous non-empty line
  used: boolean;
}

function _markerCaption(line: string): { caption: string; url: string | null } | null {
  const t = line.trim();
  const d = RE_IMAGE_DATA.exec(t);
  if (d) return { caption: d[1].trim(), url: null };
  const m = RE_IMAGE_MD.exec(t);
  if (m) return { caption: (m[1] || "").trim(), url: (m[2] || "").trim() };
  return null;
}

function _ssotMarkers(body: string): SsotMarker[] {
  const lines = (body || "").split("\n");
  const out: SsotMarker[] = [];
  let prev = "";
  for (const raw of lines) {
    const t = raw.trim();
    if (!t) continue;
    const m = _markerCaption(t);
    if (m) out.push({ line: t, captionKey: normKey(m.caption), contextKey: normKey(prev), used: false });
    prev = t;
  }
  return out;
}

function _splitCells(row: string): string[] {
  let body = row.trim();
  if (body.startsWith("|")) body = body.slice(1);
  if (body.endsWith("|") && !body.endsWith("\\|")) body = body.slice(0, -1);
  const cells: string[] = [];
  let cur = "";
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (ch === "\\" && body[i + 1] === "|") {
      cur += "\\|";
      i++;
      continue;
    }
    if (ch === "|") {
      cells.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  cells.push(cur.trim());
  return cells;
}

/** SSOT pipe tables keyed by their normalised header row → original cell grid. */
function _ssotTables(body: string): Map<string, string[][]> {
  const out = new Map<string, string[][]>();
  const lines = (body || "").split("\n");
  let rows: string[][] = [];
  const flush = () => {
    if (rows.length > 0) {
      const key = rows[0].map(normKey).join("|");
      if (!out.has(key)) out.set(key, rows);
    }
    rows = [];
  };
  for (const raw of lines) {
    const t = raw.trim();
    if (t.startsWith("|")) {
      if (RE_TABLE_SEP.test(t)) continue;
      rows.push(_splitCells(t));
    } else flush();
  }
  flush();
  return out;
}

function _escapeCell(s: string): string {
  return s
    .replace(/\r?\n/g, " ")
    .replace(/(?<!\\)\|/g, "\\|")
    .trim();
}

function _renderTable(grid: string[][], ssotTables: Map<string, string[][]>): string[] {
  if (grid.length === 0) return [];
  const cols = Math.max(...grid.map((r) => r.length), 1);
  const rows = grid.map((r) => Array.from({ length: cols }, (_, c) => r[c] ?? ""));
  // Keep the SSOT's cell markup (bold, escapes, sub/superscript) wherever the
  // cell's text did not change — Word only hands back plain text.
  const original = ssotTables.get(rows[0].map(normKey).join("|"));
  const out = rows.map((r, ri) =>
    r.map((cell, ci) => {
      const orig = original?.[ri]?.[ci];
      if (orig !== undefined && normKey(orig) === normKey(cell)) return orig;
      return _escapeCell(cell);
    }),
  );
  const line = (r: string[]) => `| ${r.join(" | ")} |`;
  return [line(out[0]), `| ${out[0].map(() => "---").join(" | ")} |`, ...out.slice(1).map(line)];
}

function _stampedMarker(pic: CapturedPicture): string | null {
  const title = (pic.altTitle || "").trim();
  const desc = (pic.altDescription || "").trim();
  if (title === ASTHRA_FIGURE_ALT_TITLE && desc) return `[IMAGE DATA: ${desc}]`;
  if (title.startsWith(`${ASTHRA_FIGURE_ALT_TITLE}|`)) {
    const url = title.slice(ASTHRA_FIGURE_ALT_TITLE.length + 1).trim();
    if (url) return `![${desc}](${url})`;
  }
  return null;
}

function _captionOfMarker(marker: string): string {
  return _markerCaption(marker)?.caption ?? "";
}

/**
 * Build the section's markdown body (heading EXCLUSIVE — the caller re-attaches
 * the SSOT heading line, exactly as before) from a captured snapshot.
 *
 * ``ssotBody`` is the section's current stored markdown; it is only consulted
 * to recognise legacy (unstamped) drafted figures and to keep table-cell markup.
 */
// A heading-shaped markdown line: optional ``#``s, a section number, a title.
const RE_NUMBERED_LINE = /^#{0,6}\s*(\S+?)\.?\s+\S/;

/** The captured paragraphs up to the first heading of a child section that
 *  the stored body does not hold. Word's read of a parent runs to the next
 *  same-or-higher heading, so it takes in every child section below it — each
 *  saved under its own module — and a Save stored them all again as the
 *  parent's text. A numbered sub-heading the stored body already has
 *  (12.3.2.1 Deaths inside 12.3.2) is the section's own and stays. */
export function clipChildSections(section: CapturedSection, ssotBody: string): CapturedParagraph[] {
  const prefix = `${section.sectionNumber}.`;
  const own = new Set<string>();
  for (const line of ssotBody.split("\n")) {
    const m = RE_NUMBERED_LINE.exec(line.trim());
    if (m?.[1].startsWith(prefix)) own.add(m[1]);
  }
  const paras = section.paragraphs;
  const cut = paras.findIndex((p) => !!p.headingNumber?.startsWith(prefix) && !own.has(p.headingNumber));
  return cut < 0 ? paras : paras.slice(0, cut);
}

export function buildSectionMarkdown(section: CapturedSection, ssotBody: string): BuiltSection {
  const paras = clipChildSections(section, ssotBody);
  const markers = _ssotMarkers(ssotBody);
  const ssotTables = _ssotTables(ssotBody);
  const uploads: FigureUpload[] = [];
  const stamps: FigureStamp[] = [];
  let deferred = false;

  const takeMarker = (pred: (m: SsotMarker) => boolean): SsotMarker | null => {
    const m = markers.find((x) => !x.used && pred(x));
    if (m) m.used = true;
    return m ?? null;
  };

  // Previous / next non-empty BODY paragraph text (tables excluded).
  const prevBodyText = (i: number): string => {
    for (let j = i - 1; j >= 0; j--) {
      if (paras[j].table) return "";
      if (paras[j].text) return paras[j].text;
      if (paras[j].pictures?.length) return "";
    }
    return "";
  };
  const nextBodyIndex = (i: number): number => {
    for (let j = i + 1; j < paras.length; j++) {
      if (paras[j].table) return -1;
      if (paras[j].text) return j;
      if (paras[j].pictures?.length) return -1;
    }
    return -1;
  };

  // ── Pass 1: resolve every picture (and placeholder paragraph) to a marker ──
  const markerByOrdinal = new Map<number, string>();
  const placeholderMarker = new Map<number, string>(); // paragraph index → marker
  const unresolved: { pic: CapturedPicture; para: number }[] = [];

  // Stamped pictures first, so their markers are claimed before any heuristic.
  for (const p of paras) {
    for (const pic of p.pictures ?? []) {
      const m = _stampedMarker(pic);
      if (!m) continue;
      markerByOrdinal.set(pic.ordinal, m);
      const key = normKey(_captionOfMarker(m));
      takeMarker((x) => x.captionKey === key);
    }
  }

  for (let i = 0; i < paras.length; i++) {
    const p = paras[i];
    // An unresolved-image placeholder paragraph is still a figure.
    if (!p.table && !p.pictures?.length && p.text) {
      const ph = RE_ADDIN_PLACEHOLDER.exec(p.text);
      if (ph) {
        placeholderMarker.set(i, `[IMAGE DATA: ${ph[1]}]`);
        takeMarker((x) => x.captionKey === normKey(ph[1]));
        continue;
      }
      const br = RE_BRACKETED.exec(p.text);
      if (br && markers.some((x) => !x.used && x.captionKey === normKey(br[1]))) {
        const m = takeMarker((x) => x.captionKey === normKey(br[1]));
        if (m) placeholderMarker.set(i, m.line);
        continue;
      }
    }
    for (const pic of p.pictures ?? []) {
      if (markerByOrdinal.has(pic.ordinal)) continue;
      // Legacy drafted figure: its auto-caption line follows it…
      const nextIdx = p.table ? -1 : nextBodyIndex(i);
      const nextKey = nextIdx >= 0 ? normKey(paras[nextIdx].text) : "";
      let m = nextKey ? takeMarker((x) => x.captionKey === nextKey) : null;
      // …or its "Figure N. …" line precedes it.
      if (!m && !p.table) {
        const prevKey = normKey(prevBodyText(i));
        if (prevKey) m = takeMarker((x) => x.contextKey === prevKey);
      }
      if (m) {
        markerByOrdinal.set(pic.ordinal, m.line);
        continue;
      }
      unresolved.push({ pic, para: i });
    }
  }

  // Positional fallback: exactly as many unexplained pictures as unclaimed SSOT
  // markers → they are those figures, in order.
  const leftover = markers.filter((x) => !x.used);
  if (unresolved.length > 0 && leftover.length === unresolved.length) {
    for (const [k, { pic }] of unresolved.entries()) {
      leftover[k].used = true;
      markerByOrdinal.set(pic.ordinal, leftover[k].line);
    }
    unresolved.length = 0;
  }

  // Whatever is left was inserted by the writer: title it and upload it.
  const usedCaptions = new Set<string>(
    [...markerByOrdinal.values(), ...placeholderMarker.values()].map((m) => normKey(_captionOfMarker(m))),
  );
  let handCount = 0;
  for (const { pic, para } of unresolved) {
    handCount += 1;
    const p = paras[para];
    let title = "";
    if (!p.table) {
      const prev = prevBodyText(para);
      if (RE_FIGURE_CAPTION.test(prev)) title = prev;
      if (!title) {
        const n = nextBodyIndex(para);
        if (n >= 0 && RE_FIGURE_CAPTION.test(paras[n].text)) title = paras[n].text;
      }
    }
    if (!title && pic.altTitle.trim() && !pic.altTitle.startsWith(ASTHRA_FIGURE_ALT_TITLE)) title = pic.altTitle.trim();
    if (!title && pic.altDescription.trim()) title = pic.altDescription.trim().split("\n")[0];
    if (!title) title = `Figure ${section.sectionNumber}-${handCount}`;
    title = title.replace(/[[\]]/g, "").replace(/\s+/g, " ").trim().slice(0, 200);
    let unique = title;
    for (let k = 2; usedCaptions.has(normKey(unique)); k++) unique = `${title} (${k})`;
    usedCaptions.add(normKey(unique));

    markerByOrdinal.set(pic.ordinal, `[IMAGE DATA: ${unique}]`);
    if (!pic.base64 || pic.loadingPlaceholder) {
      deferred = true;
      continue;
    }
    uploads.push({ ordinal: pic.ordinal, caption: unique, base64: pic.base64, format: pic.format });
  }

  // Stamp every picture that doesn't carry its identity yet.
  for (const p of paras) {
    for (const pic of p.pictures ?? []) {
      if (_stampedMarker(pic)) continue;
      const marker = markerByOrdinal.get(pic.ordinal);
      const parsed = marker ? _markerCaption(marker) : null;
      if (!parsed) continue;
      stamps.push({
        ordinal: pic.ordinal,
        title: parsed.url ? `${ASTHRA_FIGURE_ALT_TITLE}|${parsed.url}` : ASTHRA_FIGURE_ALT_TITLE,
        description: parsed.caption,
      });
    }
  }

  // ── Pass 2: emit ──────────────────────────────────────────────────────────
  // One block per Word paragraph, separated by a blank line — markdown's
  // paragraph break, which the resolve pass splits flags on. A single "\n"
  // made a whole saved section one paragraph (every flag in it, one rewrite).
  const blocks: string[] = [];
  const skip = new Set<number>();
  let i = 0;
  while (i < paras.length) {
    const p = paras[i];
    if (skip.has(i)) {
      i++;
      continue;
    }
    if (p.table) {
      // Gather this table's contiguous paragraphs into a grid.
      const t = p.table.t;
      const grid: string[][] = [];
      while (i < paras.length && paras[i].table && paras[i].table?.t === t) {
        const cp = paras[i];
        const { row, col } = cp.table as { row: number; col: number };
        while (grid.length <= row) grid.push([]);
        const parts: string[] = [];
        if (cp.text) parts.push(cp.text);
        for (const pic of cp.pictures ?? []) {
          const m = markerByOrdinal.get(pic.ordinal);
          if (m) parts.push(m);
        }
        const prior = grid[row][col];
        const joined = parts.join(" ");
        grid[row][col] = prior ? (joined ? `${prior} ${joined}` : prior) : joined;
        i++;
      }
      for (const r of grid) for (let c = 0; c < r.length; c++) if (r[c] === undefined) r[c] = "";
      blocks.push(_renderTable(grid, ssotTables).join("\n"));
      continue;
    }
    const ph = placeholderMarker.get(i);
    if (ph) {
      blocks.push(ph);
      const n = nextBodyIndex(i);
      if (n >= 0 && normKey(paras[n].text) === normKey(_captionOfMarker(ph))) skip.add(n);
      i++;
      continue;
    }
    if (p.text) blocks.push(p.text);
    const pics = p.pictures ?? [];
    for (const pic of pics) {
      const m = markerByOrdinal.get(pic.ordinal);
      if (m) blocks.push(m);
    }
    if (pics.length > 0) {
      // Drop the caption line Asthra draws under each figure — it is the
      // figure's own caption, re-drawn on every Load, not writer prose.
      const n = nextBodyIndex(i);
      const last = markerByOrdinal.get(pics[pics.length - 1].ordinal);
      if (n >= 0 && last && normKey(paras[n].text) === normKey(_captionOfMarker(last))) skip.add(n);
    }
    i++;
  }

  const markdown = blocks
    .filter((b) => b.trim())
    .join("\n\n")
    .trim();
  return { markdown, uploads, stamps, deferred };
}
